import React, { useState, useEffect } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useFamilyContext } from '../../../../contexts/FamilyContext';
import { supabase } from '../../../../lib/supabase';
import {
  Brain, FileText, Eye, Users, Target, CheckCircle, AlertCircle, Clock, Save,
  User, Settings, ChevronRight, ChevronLeft, GraduationCap, TestTube
} from 'lucide-react';

const AssessmentSection = () => {
  const { selectedStudent, studentProgress } = useParentPortal();
  const { currentUser, family } = useFamilyContext();
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  // Academic Profile State
  const [academicProfile, setAcademicProfile] = useState({
    graduationYear: '',
    highSchool: '',
    state: '',
    intendedMajor: '',
    currentGPA: '',
    weightedGPA: '',
    gpaScale: '4.0',
    satTotal: '',
    satMath: '',
    satERW: '',
    actComposite: '',
    actEnglish: '',
    actMath: '',
    actReading: '',
    actScience: '',
    academicInterests: '',
    careerGoals: '',
    extracurriculars: '',
    leadership: '',
    volunteering: '',
    workExperience: ''
  });

  // Personality Profile State
  const [personalityProfile, setPersonalityProfile] = useState({
    motivationStyle: '',
    communicationPreference: '',
    resistancePatterns: '',
    energySource: '',
    decisionMaking: '',
    feedbackStyle: '',
    identityFramework: ''
  });

  // Parent Observations State
  const [parentObservations, setParentObservations] = useState({
    parentObservationEasySubject: '',
    parentObservationGrowth: '',
    parentObservationPoorFit: '',
    parentObservationSurprises: '',
    parentObservationAgeLimit: '',
    parentObservationRulesQuestioned: '',
    parentObservationWrongLevel: '',
    parentObservationSelfTaught: '',
    parentObservationObsessions: '',
    parentObservationUnusualInterests: '',
    parentObservationHiddenQualities: ''
  });

  // Personality assessment questions
  const personalityQuestions = [
    {
      id: 'motivationStyle',
      question: `What primarily drives ${selectedStudent?.name || 'your child'}?`,
      options: [
        { value: 'achievement', label: 'Achievement & Recognition - wants to excel and be acknowledged' },
        { value: 'curiosity', label: 'Curiosity & Understanding - driven to figure things out and learn' },
        { value: 'impact', label: 'Impact & Contribution - motivated by making a difference' }
      ]
    },
    {
      id: 'communicationPreference',
      question: `How does ${selectedStudent?.name || 'your child'} best receive and process information?`,
      options: [
        { value: 'data-logic', label: 'Data & Logic - responds to facts, evidence, and logical reasoning' },
        { value: 'stories-examples', label: 'Stories & Examples - learns through narratives and concrete examples' },
        { value: 'visual-hands-on', label: 'Visual & Hands-on - needs to see it or try it to understand' }
      ]
    },
    {
      id: 'resistancePatterns',
      question: `When ${selectedStudent?.name || 'your child'} resists or pushes back, it's usually because:`,
      options: [
        { value: 'authority-skeptical', label: 'Authority-skeptical - questions "because I said so" reasoning' },
        { value: 'perfectionist-paralyzed', label: 'Perfectionist-paralyzed - afraid of not doing it perfectly' },
        { value: 'overwhelm-avoidant', label: 'Overwhelm-avoidant - shuts down when there\'s too much at once' }
      ]
    },
    {
      id: 'energySource',
      question: `${selectedStudent?.name || 'Your child'} gets energized by:`,
      options: [
        { value: 'competition', label: 'Competition - thrives on winning and outperforming others' },
        { value: 'collaboration', label: 'Collaboration - energized by working with and helping others' },
        { value: 'independent-exploration', label: 'Independent exploration - needs space to figure things out alone' }
      ]
    },
    {
      id: 'decisionMaking',
      question: `When making important decisions, ${selectedStudent?.name || 'your child'} typically:`,
      options: [
        { value: 'research-heavy', label: 'Research-heavy - wants all the data before deciding' },
        { value: 'intuition-based', label: 'Intuition-based - goes with gut feeling and instinct' },
        { value: 'consensus-seeking', label: 'Consensus-seeking - wants input from trusted people' }
      ]
    },
    {
      id: 'feedbackStyle',
      question: `${selectedStudent?.name || 'Your child'} responds best to feedback that is:`,
      options: [
        { value: 'direct-analytical', label: 'Direct & Analytical - straightforward, data-based critique' },
        { value: 'supportive-encouraging', label: 'Supportive & Encouraging - positive reinforcement first' },
        { value: 'challenge-based', label: 'Challenge-based - framed as problems to solve' }
      ]
    },
    {
      id: 'identityFramework',
      question: `${selectedStudent?.name || 'Your child'} sees themselves primarily as:`,
      options: [
        { value: 'individual-achiever', label: 'Individual achiever - personal excellence and recognition' },
        { value: 'community-contributor', label: 'Community contributor - part of something bigger' },
        { value: 'system-innovator', label: 'System innovator - someone who improves how things work' }
      ]
    }
  ];

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  // Load existing profile data
  useEffect(() => {
    if (selectedStudent) {
      loadProfile();
    }
  }, [selectedStudent]);

  const loadProfile = async () => {
    if (!selectedStudent) return;

    setLoading(true);
    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;
      // Load from the student_academic_profiles table
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', studentId)
        .single();

      if (error && error.code !== 'PGRST116') { // Not found is ok
        console.error('Error loading profile:', error);
      } else if (data) {
        // Map database fields to our component state
        setAcademicProfile({
          graduationYear: data.graduation_year?.toString() || '',
          highSchool: data.high_school_name || '',
          state: data.state_of_residency || '',
          intendedMajor: data.intended_major || '',
          currentGPA: data.gpa_unweighted?.toString() || '',
          weightedGPA: data.gpa_weighted?.toString() || '',
          gpaScale: data.gpa_scale?.toString() || '4.0',
          satTotal: data.sat_total?.toString() || '',
          satMath: data.sat_math?.toString() || '',
          satERW: data.sat_reading_writing?.toString() || '',
          actComposite: data.act_composite?.toString() || '',
          actEnglish: data.act_english?.toString() || '',
          actMath: data.act_math?.toString() || '',
          actReading: data.act_reading?.toString() || '',
          actScience: data.act_science?.toString() || '',
          academicInterests: data.academic_interests?.join(', ') || '',
          careerGoals: data.career_goals || '',
          extracurriculars: data.extracurriculars || '',
          leadership: data.leadership_activities || '',
          volunteering: data.volunteer_work || '',
          workExperience: data.work_experience || ''
        });

        // Load personality profile if available
        if (data.personality_profile) {
          try {
            const savedProfile = JSON.parse(data.personality_profile);
            setPersonalityProfile(savedProfile);
          } catch (error) {
            console.log('Error parsing personality profile:', error);
          }
        }

        // Load parent observations
        setParentObservations({
          parentObservationEasySubject: data.parent_observation_easy_subject || '',
          parentObservationGrowth: data.parent_observation_growth || '',
          parentObservationPoorFit: data.parent_observation_poor_fit || '',
          parentObservationSurprises: data.parent_observation_surprises || '',
          parentObservationAgeLimit: data.parent_observation_age_limit || '',
          parentObservationRulesQuestioned: data.parent_observation_rules_questioned || '',
          parentObservationWrongLevel: data.parent_observation_wrong_level || '',
          parentObservationSelfTaught: data.parent_observation_self_taught || '',
          parentObservationObsessions: data.parent_observation_obsessions || '',
          parentObservationUnusualInterests: data.parent_observation_unusual_interests || '',
          parentObservationHiddenQualities: data.parent_observation_hidden_qualities || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!selectedStudent) return;

    setSaveStatus('saving');
    setSaveMessage('');

    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;

      // Prepare the complete profile data
      const updateData = {
        user_id: studentId,
        // Academic Profile fields
        graduation_year: academicProfile.graduationYear ? parseInt(academicProfile.graduationYear) : null,
        high_school_name: academicProfile.highSchool,
        state_of_residency: academicProfile.state,
        intended_major: academicProfile.intendedMajor,
        gpa_unweighted: academicProfile.currentGPA ? parseFloat(academicProfile.currentGPA) : null,
        gpa_weighted: academicProfile.weightedGPA ? parseFloat(academicProfile.weightedGPA) : null,
        gpa_scale: academicProfile.gpaScale ? parseFloat(academicProfile.gpaScale) : 4.0,
        sat_total: academicProfile.satTotal ? parseInt(academicProfile.satTotal) : null,
        sat_math: academicProfile.satMath ? parseInt(academicProfile.satMath) : null,
        sat_reading_writing: academicProfile.satERW ? parseInt(academicProfile.satERW) : null,
        act_composite: academicProfile.actComposite ? parseInt(academicProfile.actComposite) : null,
        act_english: academicProfile.actEnglish ? parseInt(academicProfile.actEnglish) : null,
        act_math: academicProfile.actMath ? parseInt(academicProfile.actMath) : null,
        act_reading: academicProfile.actReading ? parseInt(academicProfile.actReading) : null,
        act_science: academicProfile.actScience ? parseInt(academicProfile.actScience) : null,
        academic_interests: academicProfile.academicInterests ? academicProfile.academicInterests.split(',').map(i => i.trim()).filter(Boolean) : [],
        career_goals: academicProfile.careerGoals,
        extracurriculars: academicProfile.extracurriculars,
        leadership_activities: academicProfile.leadership,
        volunteer_work: academicProfile.volunteering,
        work_experience: academicProfile.workExperience,
        // Personality Profile (as JSON string)
        personality_profile: JSON.stringify(personalityProfile),
        // Parent observation fields
        parent_observation_easy_subject: parentObservations.parentObservationEasySubject,
        parent_observation_growth: parentObservations.parentObservationGrowth,
        parent_observation_poor_fit: parentObservations.parentObservationPoorFit,
        parent_observation_surprises: parentObservations.parentObservationSurprises,
        parent_observation_age_limit: parentObservations.parentObservationAgeLimit,
        parent_observation_rules_questioned: parentObservations.parentObservationRulesQuestioned,
        parent_observation_wrong_level: parentObservations.parentObservationWrongLevel,
        parent_observation_self_taught: parentObservations.parentObservationSelfTaught,
        parent_observation_obsessions: parentObservations.parentObservationObsessions,
        parent_observation_unusual_interests: parentObservations.parentObservationUnusualInterests,
        parent_observation_hidden_qualities: parentObservations.parentObservationHiddenQualities,
        updated_at: new Date().toISOString()
      };

      // Save to the existing student_academic_profiles table
      const { error } = await supabase
        .from('student_academic_profiles')
        .upsert(updateData, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setSaveStatus('success');
      setSaveMessage('Profile kickstart saved successfully!');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to save profile. Please try again.');
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const calculateCompletion = () => {
    // Academic profile completion
    const academicFields = ['graduationYear', 'highSchool', 'state', 'intendedMajor', 'currentGPA'];
    const academicComplete = academicFields.filter(field => academicProfile[field]).length;

    // Personality profile completion
    const personalityComplete = Object.values(personalityProfile).filter(val => val).length;

    // Parent observations completion
    const observationFields = Object.keys(parentObservations);
    const observationsComplete = observationFields.filter(field => parentObservations[field]).length;

    return {
      academic: { complete: academicComplete, total: academicFields.length },
      personality: { complete: personalityComplete, total: personalityQuestions.length },
      observations: { complete: observationsComplete, total: observationFields.length }
    };
  };

  const completion = calculateCompletion();
  const totalComplete = completion.academic.complete + completion.personality.complete + completion.observations.complete;
  const totalPossible = completion.academic.total + completion.personality.total + completion.observations.total;
  const percentComplete = Math.round((totalComplete / totalPossible) * 100);

  if (!selectedStudent) {
    return (
      <div className="text-center py-12">
        <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          No Student Selected
        </h2>
        <p className="text-slate-600">
          Select a student to access strategic assessment tools.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Strategic Assessment Kickstart</h1>
        <p className="text-slate-600 mt-1">
          Help {selectedStudent.name} get started by providing initial profile information
        </p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Profile Completion</h3>
          <span className="text-2xl font-bold text-teal-600">{percentComplete}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3 mb-6">
          <div
            className="bg-gradient-to-r from-teal-500 to-teal-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${percentComplete}%` }}
          />
        </div>

        {/* Step Navigation */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { num: 1, label: 'Academic Profile', complete: completion.academic.complete, total: completion.academic.total },
            { num: 2, label: 'Personality', complete: completion.personality.complete, total: completion.personality.total },
            { num: 3, label: 'Observations', complete: completion.observations.complete, total: completion.observations.total },
            { num: 4, label: 'Review', complete: percentComplete === 100 ? 1 : 0, total: 1 }
          ].map((step) => (
            <button
              key={step.num}
              onClick={() => setCurrentStep(step.num)}
              className={`p-3 rounded-lg border transition-all ${
                currentStep === step.num
                  ? 'bg-teal-50 border-teal-500'
                  : step.complete === step.total
                  ? 'bg-emerald-50 border-emerald-300'
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="text-xs font-medium text-slate-600 mb-1">Step {step.num}</div>
              <div className="font-semibold text-slate-900">{step.label}</div>
              <div className="text-xs text-slate-500 mt-1">
                {step.complete}/{step.total} complete
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Step Content */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-3/4"></div>
          <div className="h-32 bg-slate-200 rounded"></div>
        </div>
      ) : (
        <>
          {/* Step 1: Academic Profile */}
          {currentStep === 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Academic Profile</h3>
                    <p className="text-sm text-slate-600">Basic academic information and achievements</p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Graduation Year
                    </label>
                    <input
                      type="text"
                      value={academicProfile.graduationYear}
                      onChange={(e) => setAcademicProfile({...academicProfile, graduationYear: e.target.value})}
                      placeholder="2025"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      State of Residency
                    </label>
                    <select
                      value={academicProfile.state}
                      onChange={(e) => setAcademicProfile({...academicProfile, state: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                    >
                      <option value="">Select state</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    High School Name
                  </label>
                  <input
                    type="text"
                    value={academicProfile.highSchool}
                    onChange={(e) => setAcademicProfile({...academicProfile, highSchool: e.target.value})}
                    placeholder="Lincoln High School"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                  />
                </div>

                {/* GPA Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Unweighted GPA
                    </label>
                    <input
                      type="text"
                      value={academicProfile.currentGPA}
                      onChange={(e) => setAcademicProfile({...academicProfile, currentGPA: e.target.value})}
                      placeholder="3.85"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Weighted GPA
                    </label>
                    <input
                      type="text"
                      value={academicProfile.weightedGPA}
                      onChange={(e) => setAcademicProfile({...academicProfile, weightedGPA: e.target.value})}
                      placeholder="4.25"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      GPA Scale
                    </label>
                    <select
                      value={academicProfile.gpaScale}
                      onChange={(e) => setAcademicProfile({...academicProfile, gpaScale: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                    >
                      <option value="4.0">4.0</option>
                      <option value="5.0">5.0</option>
                      <option value="6.0">6.0</option>
                    </select>
                  </div>
                </div>

                {/* Test Scores */}
                <div>
                  <h4 className="font-medium text-slate-800 mb-3">Test Scores (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-4">
                      <h5 className="text-sm font-medium text-slate-700">SAT Scores</h5>
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={academicProfile.satTotal}
                          onChange={(e) => setAcademicProfile({...academicProfile, satTotal: e.target.value})}
                          placeholder="Total (1600)"
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                        <input
                          type="text"
                          value={academicProfile.satMath}
                          onChange={(e) => setAcademicProfile({...academicProfile, satMath: e.target.value})}
                          placeholder="Math (800)"
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                        <input
                          type="text"
                          value={academicProfile.satERW}
                          onChange={(e) => setAcademicProfile({...academicProfile, satERW: e.target.value})}
                          placeholder="ERW (800)"
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h5 className="text-sm font-medium text-slate-700">ACT Scores</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={academicProfile.actComposite}
                          onChange={(e) => setAcademicProfile({...academicProfile, actComposite: e.target.value})}
                          placeholder="Composite (36)"
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                        <input
                          type="text"
                          value={academicProfile.actEnglish}
                          onChange={(e) => setAcademicProfile({...academicProfile, actEnglish: e.target.value})}
                          placeholder="English"
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                        <input
                          type="text"
                          value={academicProfile.actMath}
                          onChange={(e) => setAcademicProfile({...academicProfile, actMath: e.target.value})}
                          placeholder="Math"
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                        <input
                          type="text"
                          value={academicProfile.actReading}
                          onChange={(e) => setAcademicProfile({...academicProfile, actReading: e.target.value})}
                          placeholder="Reading"
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Interests and Goals */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Intended Major
                  </label>
                  <input
                    type="text"
                    value={academicProfile.intendedMajor}
                    onChange={(e) => setAcademicProfile({...academicProfile, intendedMajor: e.target.value})}
                    placeholder="Computer Science, Engineering, Business..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Academic Interests
                  </label>
                  <textarea
                    value={academicProfile.academicInterests}
                    onChange={(e) => setAcademicProfile({...academicProfile, academicInterests: e.target.value})}
                    placeholder="List subjects and areas of academic interest..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Career Goals
                  </label>
                  <textarea
                    value={academicProfile.careerGoals}
                    onChange={(e) => setAcademicProfile({...academicProfile, careerGoals: e.target.value})}
                    placeholder="What career paths is your child interested in pursuing?"
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Personality Profile */}
          {currentStep === 2 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Brain className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Personality Profile</h3>
                    <p className="text-sm text-slate-600">
                      Understanding how {selectedStudent.name} thinks and learns helps create more targeted strategies
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {personalityQuestions.map((question, index) => (
                  <div key={question.id} className="border-b border-slate-200 pb-6 last:border-0">
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      {index + 1}. {question.question}
                    </label>
                    <div className="space-y-2">
                      {question.options.map(option => (
                        <label key={option.value} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer">
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={personalityProfile[question.id] === option.value}
                            onChange={(e) => setPersonalityProfile({
                              ...personalityProfile,
                              [question.id]: e.target.value
                            })}
                            className="mt-1 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-slate-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Parent Observations */}
          {currentStep === 3 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Parent Observations & Insights</h3>
                  <p className="text-sm text-slate-600">
                    These questions help us understand what makes {selectedStudent.name} unique beyond grades and test scores
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Academic & Learning Profile */}
                <div>
                  <h4 className="font-medium text-slate-800 mb-4">Academic & Learning Profile</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        What subject does {selectedStudent.name} find surprisingly easy that others struggle with?
                      </label>
                      <textarea
                        value={parentObservations.parentObservationEasySubject}
                        onChange={(e) => setParentObservations({...parentObservations, parentObservationEasySubject: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                        rows="3"
                        placeholder="E.g., They grasp abstract math concepts immediately while classmates need multiple explanations..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Where have you seen the biggest academic growth or breakthrough?
                      </label>
                      <textarea
                        value={parentObservations.parentObservationGrowth}
                        onChange={(e) => setParentObservations({...parentObservations, parentObservationGrowth: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                        rows="3"
                        placeholder="E.g., Struggled with writing until 10th grade, then suddenly started writing beautifully..."
                      />
                    </div>
                  </div>
                </div>

                {/* System Interactions & Edge Cases */}
                <div>
                  <h4 className="font-medium text-slate-800 mb-4">System Interactions & Edge Cases</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Describe a time when school/traditional structures seemed like a poor fit for {selectedStudent.name}
                      </label>
                      <textarea
                        value={parentObservations.parentObservationPoorFit}
                        onChange={(e) => setParentObservations({...parentObservations, parentObservationPoorFit: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                        rows="3"
                        placeholder="E.g., The gifted program was too slow, regular classes too easy, no good fit anywhere..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        What does {selectedStudent.name} do that consistently surprises adults?
                      </label>
                      <textarea
                        value={parentObservations.parentObservationSurprises}
                        onChange={(e) => setParentObservations({...parentObservations, parentObservationSurprises: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                        rows="3"
                        placeholder="E.g., Adults are shocked when they debate complex topics like an adult..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        What rules or systems has {selectedStudent.name} questioned that made you think 'actually, they have a point'?
                      </label>
                      <textarea
                        value={parentObservations.parentObservationRulesQuestioned}
                        onChange={(e) => setParentObservations({...parentObservations, parentObservationRulesQuestioned: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                        rows="3"
                        placeholder="E.g., Questioned why they couldn't test out of required classes..."
                      />
                    </div>
                  </div>
                </div>

                {/* Natural Tendencies & Uniqueness */}
                <div>
                  <h4 className="font-medium text-slate-800 mb-4">Natural Tendencies & Uniqueness</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        What does {selectedStudent.name} get obsessed with or spend way too much time on?
                      </label>
                      <textarea
                        value={parentObservations.parentObservationObsessions}
                        onChange={(e) => setParentObservations({...parentObservations, parentObservationObsessions: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                        rows="3"
                        placeholder="E.g., Researches obscure historical events for hours..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        What do you hope college admissions officers will understand about {selectedStudent.name}?
                      </label>
                      <textarea
                        value={parentObservations.parentObservationHiddenQualities}
                        onChange={(e) => setParentObservations({...parentObservations, parentObservationHiddenQualities: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                        rows="4"
                        placeholder="E.g., Their creativity isn't captured by standardized tests..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Save */}
          {currentStep === 4 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Review & Save</h3>
                  <p className="text-sm text-slate-600">Review the information before saving</p>
                </div>
              </div>

              {/* Completion Summary */}
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-900 mb-3">Profile Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="font-medium text-slate-700">Academic Profile</div>
                      <div className="text-slate-600">{completion.academic.complete}/{completion.academic.total} fields complete</div>
                    </div>
                    <div>
                      <div className="font-medium text-slate-700">Personality Profile</div>
                      <div className="text-slate-600">{completion.personality.complete}/{completion.personality.total} questions answered</div>
                    </div>
                    <div>
                      <div className="font-medium text-slate-700">Parent Observations</div>
                      <div className="text-slate-600">{completion.observations.complete}/{completion.observations.total} insights provided</div>
                    </div>
                  </div>
                </div>

                {percentComplete < 100 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-amber-800">Incomplete Profile</span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      You've completed {percentComplete}% of the profile. Consider going back to fill in more information for better assessment results.
                    </p>
                  </div>
                )}
              </div>

              {/* Save Section */}
              <div className="pt-6 border-t border-slate-200">
                {/* Success/Error Message */}
                {saveStatus && (
                  <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                    saveStatus === 'success' ? 'bg-emerald-50 border border-emerald-200' :
                    saveStatus === 'error' ? 'bg-red-50 border border-red-200' :
                    'bg-azure-50 border border-azure-200'
                  }`}>
                    {saveStatus === 'success' && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                    {saveStatus === 'error' && <AlertCircle className="w-5 h-5 text-red-600" />}
                    {saveStatus === 'saving' && <Clock className="w-5 h-5 text-azure-600 animate-spin" />}
                    <span className={
                      saveStatus === 'success' ? 'text-emerald-700' :
                      saveStatus === 'error' ? 'text-red-700' :
                      'text-azure-700'
                    }>
                      {saveMessage || (saveStatus === 'saving' ? 'Saving profile...' : '')}
                    </span>
                  </div>
                )}

                <button
                  onClick={saveProfile}
                  disabled={saveStatus === 'saving'}
                  className="bg-teal-600 hover:bg-teal-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-all duration-150 active:scale-[0.98] border-2 border-slate-900 shadow-sm flex items-center gap-2"
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Clock className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Save Profile Kickstart
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:text-slate-900 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
            )}
            {currentStep < 4 && (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="ml-auto flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </>
      )}

      {/* Testing Hub Link */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-magenta-100 rounded-lg flex items-center justify-center">
            <TestTube className="w-5 h-5 text-magenta-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Testing Hub</h3>
            <p className="text-sm text-slate-600">Test assessment scenarios with mock data</p>
          </div>
        </div>
        <p className="text-slate-600 mb-4">
          Use the Testing Hub to practice the assessment process with {selectedStudent.name} or test different scenarios with mock profiles.
        </p>
        <a
          href="/parent-testing"
          className="w-full px-4 py-2 bg-magenta-600 text-white rounded-lg hover:bg-magenta-700 transition-colors inline-block text-center"
        >
          Open Testing Hub
        </a>

        {/* Show assessment status if completed */}
        {studentProgress?.hasAssessment && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">
                {selectedStudent.name} has completed their assessment
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Strategic Guidance */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl border border-indigo-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-indigo-900 mb-2">
              Your Strategic Role as a Parent
            </h3>
            <p className="text-indigo-800 mb-3">
              Your observations provide crucial insights that {selectedStudent.name} might not see in themselves.
              This strategic foundation will guide college selection, essay development, and application strategy.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white/50 rounded-lg p-3">
                <h4 className="font-medium text-indigo-900">What to Observe</h4>
                <ul className="text-indigo-700 mt-1 space-y-1">
                  <li>• Natural strengths and talents</li>
                  <li>• Character traits in action</li>
                  <li>• Moments of genuine engagement</li>
                </ul>
              </div>
              <div className="bg-white/50 rounded-lg p-3">
                <h4 className="font-medium text-indigo-900">Strategic Impact</h4>
                <ul className="text-indigo-700 mt-1 space-y-1">
                  <li>• Informs college fit decisions</li>
                  <li>• Guides essay topic selection</li>
                  <li>• Shapes application positioning</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentSection;